import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { getErrorMessage } from '../utils/errorHandler';
import { Loader2, Mail, CheckCircle, AlertCircle, ArrowLeft, RefreshCw } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const BusinessEmailVerify = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshUser } = useAuth();
  
  const businessId = searchParams.get('business_id');
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [verificationMethod, setVerificationMethod] = useState('otp');
  const [email, setEmail] = useState('');

  // If we have a token in URL, auto-verify (link verification)
  useEffect(() => {
    if (token && businessId) {
      handleLinkVerification();
    }
  }, [token, businessId]);

  const handleLinkVerification = async () => {
    setVerifying(true);
    setError('');
    
    try {
      const response = await axios.post(
        `${API}/business/${businessId}/email/verify`,
        { token },
        { withCredentials: true }
      );
      
      setSuccess(true);
      setEmail(response.data.email);
      await refreshUser();
      
      // Redirect to dashboard after 3 seconds
      setTimeout(() => {
        navigate('/business/dashboard');
      }, 3000);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setVerifying(false);
    }
  };

  const handleOtpVerification = async (e) => {
    e.preventDefault();
    if (!otp.trim()) {
      setError('Please enter the verification code');
      return;
    }
    
    setVerifying(true);
    setError('');
    
    try {
      const response = await axios.post(
        `${API}/business/${businessId}/email/verify`,
        { otp },
        { withCredentials: true }
      );
      
      setSuccess(true);
      setEmail(response.data.email);
      await refreshUser();
      
      // Redirect to dashboard after 3 seconds
      setTimeout(() => {
        navigate('/business/dashboard');
      }, 3000);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setVerifying(false);
    }
  };

  const handleResendVerification = async (method) => {
    setResending(true);
    setError('');
    
    try {
      await axios.post(
        `${API}/business/${businessId}/email/send-verification`,
        { method },
        { withCredentials: true }
      );
      
      setVerificationMethod(method);
      alert(method === 'otp' 
        ? 'A new verification code has been sent to your email.'
        : 'A new verification link has been sent to your email.'
      );
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setResending(false);
    }
  };

  // Loading state for link verification
  if (token && verifying) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <Card className="bg-gray-800 border-gray-700 max-w-md w-full mx-4">
          <CardContent className="pt-6 text-center">
            <Loader2 className="h-12 w-12 animate-spin text-yellow-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Verifying Email...</h2>
            <p className="text-gray-400">Please wait while we verify your business email.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <Card className="bg-gray-800 border-gray-700 max-w-md w-full mx-4">
          <CardContent className="pt-6 text-center">
            <div className="mx-auto w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Email Verified!</h2>
            <p className="text-gray-400 mb-4">
              Your business email <span className="text-yellow-500">{email}</span> has been verified successfully.
            </p>
            <p className="text-sm text-gray-500">Redirecting to dashboard...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // No business ID provided
  if (!businessId) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <Card className="bg-gray-800 border-gray-700 max-w-md w-full mx-4">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Invalid Request</h2>
            <p className="text-gray-400 mb-4">
              No business ID provided. Please use the verification link sent to your email.
            </p>
            <Button
              onClick={() => navigate('/business/dashboard')}
              className="bg-yellow-500 hover:bg-yellow-600 text-black"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Go to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center py-8">
      <Card className="bg-gray-800 border-gray-700 max-w-md w-full mx-4">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mb-4">
            <Mail className="h-8 w-8 text-yellow-500" />
          </div>
          <CardTitle className="text-2xl text-white">Verify Business Email</CardTitle>
          <CardDescription className="text-gray-400">
            Enter the 6-digit code sent to your business email
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm flex items-center gap-2">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <form onSubmit={handleOtpVerification} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="otp" className="text-gray-300">Verification Code</Label>
              <Input
                id="otp"
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                className="bg-gray-700 border-gray-600 text-white text-center text-2xl tracking-widest"
                maxLength={6}
                data-testid="otp-input"
              />
              <p className="text-xs text-gray-500">
                Enter the 6-digit code from your email
              </p>
            </div>

            <Button
              type="submit"
              disabled={verifying || otp.length !== 6}
              className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-semibold"
              data-testid="verify-otp-btn"
            >
              {verifying ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Verifying...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Verify Email
                </>
              )}
            </Button>
          </form>

          <div className="border-t border-gray-700 pt-4">
            <p className="text-sm text-gray-400 text-center mb-3">
              Didn&apos;t receive the code?
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => handleResendVerification('otp')}
                disabled={resending}
                className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-700"
              >
                {resending ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                Resend Code
              </Button>
              <Button
                variant="outline"
                onClick={() => handleResendVerification('link')}
                disabled={resending}
                className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-700"
              >
                <Mail className="h-4 w-4 mr-2" />
                Send Link
              </Button>
            </div>
          </div>

          <Button
            variant="ghost"
            onClick={() => navigate('/business/dashboard')}
            className="w-full text-gray-400 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default BusinessEmailVerify;
